import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  ActionPerformed,
  PushNotifications,
  Token
} from '@capacitor/push-notifications';
import { isAndroid, isMobile } from '@shared/utils/platform.utils';
import { PushRemote, PushPlatform } from './push.remote';

const LAST_TOKEN_STORAGE_KEY = 'push-token';

/**
 * FCM push notifications (Capacitor).
 *
 * Lifecycle:
 *  - `setupListeners()` runs once at app start so a notification tap that cold
 *    starts the app is not missed.
 *  - `requestPermissionAndRegister()` runs after login / on app start with a
 *    logged user (never before login): asks permission, registers with FCM and
 *    sends the token to the backend. Called on every start so the token is
 *    refreshed server-side (`last_seen_at`).
 *  - `unregister()` runs on logout: best-effort delete of the token in the
 *    backend. Stale tokens are also cleaned server-side after each campaign
 *    send (FCM invalid-token cleanup) and re-assigned if another user logs in
 *    on the same device.
 *
 * iOS: the code path is identical (APNs behind FCM), but it stays INACTIVE
 * until INDIBA provides the APNs key for the MyIndiba bundle and it is
 * uploaded to the Firebase project. Until then iOS registration fails
 * silently and is logged as a warning. No web push in phase 1.
 */
@Injectable({
  providedIn: 'root'
})
export class PushService {
  private listenersReady = false;

  constructor(private readonly pushRemote: PushRemote, private readonly router: Router) {}

  setupListeners(): void {
    if (!isMobile() || this.listenersReady) {
      return;
    }
    this.listenersReady = true;

    PushNotifications.addListener('registration', (token: Token) => {
      this.sendTokenToBackend(token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      // On iOS this is the expected path until the APNs key is available.
      console.warn('[PUSH] Registration error', error);
    });

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        this.handleNotificationTap(action.notification?.data ?? {});
      }
    );
  }

  async requestPermissionAndRegister(): Promise<void> {
    if (!isMobile()) {
      return;
    }

    try {
      let status = await PushNotifications.checkPermissions();
      if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
        status = await PushNotifications.requestPermissions();
      }
      if (status.receive !== 'granted') {
        return;
      }

      this.setupListeners();
      await PushNotifications.register();
    } catch (error) {
      console.warn('[PUSH] Could not register for push notifications', error);
    }
  }

  async unregister(): Promise<void> {
    if (!isMobile()) {
      return;
    }

    const lastToken = localStorage.getItem(LAST_TOKEN_STORAGE_KEY);
    localStorage.removeItem(LAST_TOKEN_STORAGE_KEY);

    if (lastToken) {
      try {
        await firstValueFrom(this.pushRemote.deleteToken(lastToken));
      } catch {
        // Best-effort: the backend also removes invalid tokens after sends.
      }
    }
  }

  private sendTokenToBackend(token: string): void {
    localStorage.setItem(LAST_TOKEN_STORAGE_KEY, token);

    const platform: PushPlatform = isAndroid() ? 'android' : 'ios';
    this.pushRemote.registerToken(token, platform).subscribe({
      error: (error) => console.warn('[PUSH] Could not register token in backend', error)
    });
  }

  private handleNotificationTap(data: Record<string, string>): void {
    const campaignId = Number(data['campaign_id']);
    if (Number.isInteger(campaignId) && campaignId > 0) {
      this.pushRemote.trackOpened(campaignId).subscribe({
        error: (error) => console.warn('[PUSH] Could not track push opening', error)
      });
    }

    const deepLink = data['deep_link'];
    if (deepLink && deepLink.startsWith('/')) {
      this.router.navigateByUrl(deepLink);
    }
  }
}
